import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { SourceFieldsDocument, SourceFieldsQuery } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { SimpleFieldType, mapFieldTypeToHumanReadable } from './fieldTypeMappings'

const DEFAULT_SORTING = { field: 'name', order: 'asc' } as const
const DEFAULT_PER_PAGE = 10

type FieldNode = NonNullable<SourceFieldsQuery['iris']['tableColumnsConnection']>['nodes'][number]

const SourceDetail = ({
  sourceId,
  hideAttributes = false,
}: {
  sourceId: string
  hideAttributes?: boolean
}) => {
  const { company } = useCurrentSession()

  const source = useGraphqlDataCollectionSource(
    {
      totalItemSummary: () => '',
      document: SourceFieldsDocument,
      mapVariables: ({ pagination }) => ({
        id: sourceId,
        page: pagination?.currentPage ?? 1,
        perPage: pagination?.perPage ?? DEFAULT_PER_PAGE,
        companyId: company?.id,
      }),
      mapData: (data: SourceFieldsQuery) => {
        return data.iris.tableColumnsConnection.nodes
      },
      mapPagination: (data: SourceFieldsQuery) => ({
        total: data.iris.tableColumnsConnection.totalCount ?? 0,
      }),
      search: undefined,
      defaultSortings: DEFAULT_SORTING,
      filters: {},
      sortings: {
        name: { label: 'Field name' },
        description: { label: 'Description' },
      },
      paginationType: 'pages',
      perPage: DEFAULT_PER_PAGE,
    },
    [sourceId]
  )

  const fieldTypeTranslations: Record<SimpleFieldType | string, string> = {
    text: i18n.t('ai_reports.field_type.text'),
    number: i18n.t('ai_reports.field_type.number'),
    date: i18n.t('ai_reports.field_type.date'),
    boolean: i18n.t('ai_reports.field_type.boolean'),
    other: i18n.t('ai_reports.field_type.other'),
  }

  const translateFieldType = (fieldType: string) => {
    return fieldTypeTranslations[fieldType] || fieldType
  }

  return (
    <OneDataCollection
      fullHeight
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: 'Field name',
                sorting: 'name',
                render: (item: FieldNode) => ({ type: 'text', value: item.name }),
              },
              ...(hideAttributes
                ? []
                : [
                    {
                      label: 'Description',
                      sorting: 'description' as const,
                      render: (item: FieldNode) => ({
                        type: 'longText' as const,
                        value: {
                          text: item.description,
                          full: true,
                        },
                      }),
                    },
                    {
                      label: 'Type',
                      render: (item: FieldNode) => {
                        const { isArray, type } = mapFieldTypeToHumanReadable(item.type)
                        const translatedType = translateFieldType(type)
                        return {
                          type: 'tag' as const,
                          value: {
                            type: 'raw',
                            label: isArray
                              ? i18n.t('ai_reports.field_type.array_of', { type: translatedType })
                              : translatedType,
                          },
                        }
                      },
                    },
                  ]),
            ],
          },
        },
      ]}
    />
  )
}

export default SourceDetail
